import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { SystemIcon } from '@monorepo/nova-icons';
import { useGlobalFiltersState } from './hooks';
import {
  StyledContainer,
  StyledFiltersContainer,
  StyledMenuItem,
  StyledSelect,
  StyledToggleButton,
  StyledToggleButtonGroup,
} from './styled';

/**
 * GlobalFilters Component
 *
 * Renders a row of filter controls backed by the shared MarketStatsFilterStore:
 * - Segmented toggle group (Funded / Payout / All)
 * - Program dropdown select
 * - Country dropdown select with globe icon
 */
export const GlobalFilters = observer(() => {
  const { t } = useTranslation('communityStats');

  const {
    activeToggle,
    activeProgram,
    activeCountry,
    toggleOptions,
    programOptions,
    countryOptions,
    handleToggleChange,
    handleProgramChange,
    handleCountryChange,
  } = useGlobalFiltersState();

  return (
    <StyledFiltersContainer>
      <StyledToggleButtonGroup
        value={activeToggle}
        exclusive
        onChange={handleToggleChange}
        aria-label={t('globalFilters.toggleAriaLabel')}
      >
        {toggleOptions.map((option) => (
          <StyledToggleButton key={option.value} value={option.value}>
            {option.label}
          </StyledToggleButton>
        ))}
      </StyledToggleButtonGroup>

      <StyledContainer>
        {programOptions.length > 0 && (
          <StyledSelect
            value={activeProgram}
            onChange={handleProgramChange}
            displayEmpty
            inputProps={{
              'aria-label': t('globalFilters.programAriaLabel'),
            }}
          >
            {programOptions.map((option) => (
              <StyledMenuItem key={option.value} value={option.value}>
                {option.label}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        )}

        {countryOptions.length > 0 && (
          <StyledSelect
            value={activeCountry}
            onChange={handleCountryChange}
            displayEmpty
            startAdornment={
              <SystemIcon iconName="Globe" size={16} color="inherit" />
            }
            inputProps={{
              'aria-label': t('globalFilters.countryAriaLabel'),
            }}
          >
            {countryOptions.map((option) => (
              <StyledMenuItem key={option.value} value={option.value}>
                {option.label}
              </StyledMenuItem>
            ))}
          </StyledSelect>
        )}
      </StyledContainer>
    </StyledFiltersContainer>
  );
});
